import { businessName, formatMoney, type Locale, organizationOf, pickLocale } from '@bb/shared';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { publicProfileQuery } from '@/features/public-booking/api';
import { ApiRequestError } from '@/lib/api';
import { ensureNamespace } from '@/lib/i18n';

export const Route = createFileRoute('/$username/')({
  loader: async ({ context, params }) => {
    await ensureNamespace('booking');
    try {
      return await context.queryClient.ensureQueryData(publicProfileQuery(params.username));
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: PublicProfilePage,
});

function PublicProfilePage() {
  const { host, eventTypes } = Route.useLoaderData();
  const { t, i18n } = useTranslation(['booking', 'common']);
  const locale = i18n.language as Locale;
  return (
    <main className="mx-auto max-w-lg px-4 py-12">
      <header className="mb-8 flex items-center gap-4">
        {host.avatarUrl ? (
          <img
            src={host.avatarUrl}
            alt=""
            className="size-14 shrink-0 rounded-full"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="size-14 shrink-0 rounded-full bg-neutral-200" aria-hidden />
        )}
        <div className="min-w-0">
          {/*
            The business first. A customer is booking with it, and which person
            they get may not be settled until they pick a time.
          */}
          <h1 className="truncate text-xl font-semibold">{businessName(host)}</h1>
          {organizationOf(host) ? (
            <p className="truncate text-sm text-neutral-700">{host.name}</p>
          ) : null}
          <p className="text-sm text-neutral-500">{t('booking:public.chooseEvent')}</p>
        </div>
      </header>
      <ul className="space-y-2">
        {eventTypes.map((et) => (
          <li key={et.id}>
            <Link
              to="/$username/$slug"
              params={{ username: host.username, slug: et.slug }}
              className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 hover:border-neutral-400"
            >
              <span
                className="h-8 w-1 shrink-0 rounded"
                style={{ backgroundColor: et.colour }}
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">
                  {pickLocale(et.name, locale, host.locale)}
                </span>
                <span className="block text-xs text-neutral-500">
                  {t('booking:eventTypes.units.minutesShort', { count: et.durationMinutes })}
                  {et.price ? ` · ${formatMoney(et.price, locale)}` : ''}
                </span>
              </span>
              <span className="text-neutral-400">›</span>
            </Link>
          </li>
        ))}
      </ul>
      {eventTypes.length === 0 ? (
        <p className="text-sm text-neutral-500">{t('booking:public.nothingToBook')}</p>
      ) : null}
    </main>
  );
}
